import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Search, Send, Tag as TagIcon, Zap } from "lucide-react";
import { ApiError, get, post, qs } from "@/lib/api";
import { qk, topicDispatch } from "@/lib/queryKeys";
import { S, T, dispatchStateLabel } from "@/lib/strings";
import { cn, formatSeconds } from "@/lib/utils";
import { useKeysetQuery } from "@/hooks/useKeyset";
import { useApiMutation } from "@/hooks/useApi";
import { useTopic } from "@/hooks/useConsoleSocket";
import { useLiveActionsDisabled } from "@/components/ConnectionBadge";
import { Guarded } from "@/components/Guarded";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { Input, Textarea } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Checkbox } from "@/components/ui/Checkbox";
import { Skeleton } from "@/components/ui/Skeleton";
import { Switch } from "@/components/ui/Switch";
import { Tooltip } from "@/components/ui/Tooltip";
import { DeviceHealthPill } from "./DeviceHealthPill";
import { ConfirmImpactDialog } from "./ConfirmImpactDialog";
import { EmptyState } from "./EmptyState";
import type {
  DeviceSummary,
  DispatchFrame,
  DispatchPreview,
  DispatchRequest,
  DispatchResponse,
  DispatchStatus,
  Page,
  TaskRow,
} from "@/types/fleet";
import { emptyTaskSpec } from "@/types/fleet";

/** Above this many devices, stagger by default rather than waking them all at once. */
const STAGGER_THRESHOLD = 20;
const DEFAULT_BATCH = 10;
const DEFAULT_INTERVAL_S = 30;

type Step = 0 | 1 | 2 | 3;
const STEP_TITLES = [S.stepPickTask, S.stepPickDevices, S.stepTiming, S.stepConfirm];

export function DispatchWizard({ initialDeviceIds = [] }: { initialDeviceIds?: string[] }) {
  const [step, setStep] = useState<Step>(0);

  // --- step 1: what to run
  const [taskMode, setTaskMode] = useState<"existing" | "adhoc">("existing");
  const [taskId, setTaskId] = useState("");
  // Values for the chosen task's `{{PLACEHOLDERS}}`, seeded from its stored defaults.
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [adhocGoal, setAdhocGoal] = useState("");

  // --- step 2: where. Both tabs write to the same set: the tag tab is a fast
  // way to tick many rows, not a second kind of selection.
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialDeviceIds));

  // --- step 3: how fast
  const [staggerChoice, setStaggerChoice] = useState(false);
  const [batch, setBatch] = useState(DEFAULT_BATCH);
  const [intervalS, setIntervalS] = useState(DEFAULT_INTERVAL_S);
  const [touchedStagger, setTouchedStagger] = useState(false);

  // --- step 4
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dispatchId, setDispatchId] = useState<string | null>(null);

  const tasks = useQuery<Page<TaskRow>, ApiError>({
    queryKey: qk.tasks(),
    queryFn: () => get<Page<TaskRow>>("/tasks"),
    staleTime: 60_000,
  });

  const devices = useKeysetQuery<DeviceSummary>(["devices", "dispatch-picker"], (cursor) =>
    `/devices${qs({ limit: 100, cursor })}`,
  );

  const selectedTask = useMemo(
    () => (taskMode === "existing" ? (tasks.data?.items ?? []).find((t) => t.id === taskId) : undefined),
    [tasks.data, taskId, taskMode],
  );
  const taskVariables = selectedTask?.variables ?? [];
  // Blank counts as unfilled, the same rule the server applies: an empty page name
  // searches for nothing and the run fails on the phone.
  const missingVariables = taskVariables.filter((v) => !(variables[v] ?? "").trim());

  const selectedList = useMemo(
    () => devices.items.filter((d) => selected.has(d.id)),
    [devices.items, selected],
  );

  // Stagger defaults on once the batch is big enough to need it, and is a plain
  // derivation until the operator touches the control — after which their choice
  // wins. Deriving rather than syncing in an effect means the default cannot
  // flicker on, and cannot silently undo a decision they already made.
  const staggerOn = touchedStagger ? staggerChoice : selected.size > STAGGER_THRESHOLD;

  const request: DispatchRequest | null = useMemo(() => {
    if (selected.size === 0) return null;
    if (taskMode === "existing" && !taskId) return null;
    if (taskMode === "adhoc" && !adhocGoal.trim()) return null;

    // "10 部 / 30 秒" is the operator's unit; the wire's is a per-device delay
    // plus a concurrency cap. 30 s ÷ 10 devices = one command every 3 s.
    const staggerMs = staggerOn && batch > 0 ? Math.round((intervalS * 1000) / batch) : 0;

    return {
      ...(taskMode === "existing"
        ? { task_id: taskId, variables }
        : {
            inline_spec: {
              ...emptyTaskSpec(),
              id: "adhoc",
              name: "Run now",
              goal: adhocGoal.trim(),
              requireUnlock: false,
            },
          }),
      target: { device_ids: [...selected] },
      stagger_ms: staggerMs,
      max_in_flight: staggerOn ? batch : Math.max(selected.size, 1),
      not_before_ms: null,
      max_retries: 1,
    };
  }, [selected, taskMode, taskId, variables, adhocGoal, staggerOn, batch, intervalS]);

  const preview = useQuery<DispatchPreview, ApiError>({
    queryKey: ["dispatch-preview", request],
    queryFn: () => post<DispatchPreview>("/dispatch/preview", request),
    enabled: confirmOpen && request !== null,
    retry: false,
    staleTime: 0,
  });

  const fire = useApiMutation<DispatchResponse, DispatchRequest>(
    (body) => post<DispatchResponse>("/dispatch", body),
    {
      errorTitle: "派發失敗",
      onSuccess: (data) => {
        setDispatchId(data.dispatch_id);
        setConfirmOpen(false);
      },
    },
  );

  const live = useLiveActionsDisabled();

  if (dispatchId) {
    return (
      <DispatchProgress
        dispatchId={dispatchId}
        onNew={() => {
          setDispatchId(null);
          setStep(0);
          setSelected(new Set());
        }}
      />
    );
  }

  const canAdvance =
    (step === 0 &&
      (taskMode === "existing"
        ? !!taskId && missingVariables.length === 0
        : adhocGoal.trim().length > 0)) ||
    (step === 1 && selected.size > 0) ||
    step === 2;

  return (
    <div className="space-y-5">
      <Stepper current={step} onJump={(s) => s < step && setStep(s)} />

      {step === 0 && (
        <TaskStep
          mode={taskMode}
          onModeChange={setTaskMode}
          taskId={taskId}
          onTaskId={(next) => {
            setTaskId(next);
            // Seed from the task's own defaults; a value typed for the previous task is not an
            // answer about this one.
            const chosen = (tasks.data?.items ?? []).find((t) => t.id === next);
            setVariables({ ...(chosen?.variable_defaults ?? {}) });
          }}
          variables={variables}
          onVariable={(name, value) => setVariables((v) => ({ ...v, [name]: value }))}
          missingVariables={missingVariables}
          goal={adhocGoal}
          onGoal={setAdhocGoal}
          tasks={tasks.data?.items ?? []}
          loading={tasks.isPending}
        />
      )}

      {step === 1 && (
        <DeviceStep
          devices={devices.items}
          loading={devices.isInitialLoading}
          canLoadMore={devices.canLoadMore}
          onLoadMore={devices.loadMore}
          selected={selected}
          onSelected={setSelected}
        />
      )}

      {step === 2 && (
        <TimingStep
          count={selected.size}
          on={staggerOn}
          onToggle={(v) => {
            setTouchedStagger(true);
            setStaggerChoice(v);
          }}
          batch={batch}
          onBatch={(v) => {
            setTouchedStagger(true);
            setBatch(v);
          }}
          intervalS={intervalS}
          onIntervalS={(v) => {
            setTouchedStagger(true);
            setIntervalS(v);
          }}
        />
      )}

      {step === 3 && (
        <ReviewStep
          taskLabel={
            taskMode === "existing"
              ? (tasks.data?.items.find((t) => t.id === taskId)?.name ?? taskId)
              : adhocGoal
          }
          adhoc={taskMode === "adhoc"}
          count={selected.size}
          stagger={staggerOn ? { batch, intervalS } : null}
        />
      )}

      <div className="flex items-center gap-2">
        {step > 0 && (
          <Button variant="ghost" onClick={() => setStep((s) => (s - 1) as Step)}>
            <ChevronLeft size={13} />
            {S.previous}
          </Button>
        )}
        <div className="flex-1" />
        {step < 3 ? (
          <Button
            variant="primary"
            disabled={!canAdvance}
            onClick={() => setStep((s) => (s + 1) as Step)}
          >
            {S.next}
            <ChevronRight size={13} />
          </Button>
        ) : (
          <Guarded permission="dispatch_run">
            <Button
              variant="primary"
              disabled={!request || live.disabled}
              title={live.reason}
              onClick={() => setConfirmOpen(true)}
            >
              <Send size={13} />
              {S.dispatchFire}
            </Button>
          </Guarded>
        )}
      </div>

      <ConfirmImpactDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={S.dispatchFire}
        description={T.nDevices(selected.size)}
        preview={preview.data ?? null}
        loading={preview.isPending}
        confirmLabel={S.dispatchFire}
        pending={fire.isPending}
        affectedNames={selectedList.map((d) => `${d.label} · ${d.id}`)}
        onConfirm={() => request && fire.mutate(request)}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Stepper
 * ------------------------------------------------------------------ */

function Stepper({ current, onJump }: { current: Step; onJump: (s: Step) => void }) {
  return (
    <ol className="flex flex-wrap items-center gap-1.5">
      {STEP_TITLES.map((title, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={title} className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={!done}
              onClick={() => onJump(i as Step)}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-medium transition-colors",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]",
                active && "bg-[rgba(255,255,255,0.06)] text-[var(--color-fg)]",
                done && "text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]",
                !active && !done && "text-[var(--color-fg-faint)]",
              )}
            >
              <span
                className={cn(
                  "grid size-4 place-items-center rounded-full text-[9px]",
                  active
                    ? "bg-[var(--color-accent-bg)] text-white"
                    : done
                      ? "bg-[rgba(16,185,129,0.15)] text-[#10b981]"
                      : "bg-[rgba(255,255,255,0.06)] text-[var(--color-fg-faint)]",
                )}
              >
                {done ? <Check size={9} /> : i + 1}
              </span>
              {title}
            </button>
            {i < STEP_TITLES.length - 1 && (
              <ChevronRight size={12} className="text-[var(--color-fg-faint)]" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* ------------------------------------------------------------------ *
 * Step 1 — task
 * ------------------------------------------------------------------ */

function TaskStep({
  mode,
  onModeChange,
  taskId,
  onTaskId,
  variables,
  onVariable,
  missingVariables,
  goal,
  onGoal,
  tasks,
  loading,
}: {
  mode: "existing" | "adhoc";
  onModeChange: (m: "existing" | "adhoc") => void;
  taskId: string;
  onTaskId: (v: string) => void;
  variables: Record<string, string>;
  onVariable: (name: string, value: string) => void;
  missingVariables: string[];
  goal: string;
  onGoal: (v: string) => void;
  tasks: TaskRow[];
  loading: boolean;
}) {
  const chosen = tasks.find((t) => t.id === taskId);
  return (
    <Card>
      <CardContent className="space-y-4 pt-4">
        <div className="flex gap-1">
          <ModeTab active={mode === "existing"} onClick={() => onModeChange("existing")}>
            {S.dispatchExistingTask}
          </ModeTab>
          <ModeTab active={mode === "adhoc"} onClick={() => onModeChange("adhoc")}>
            {S.dispatchAdhoc}
          </ModeTab>
        </div>

        {mode === "existing" ? (
          loading ? (
            <Skeleton className="h-9 w-full max-w-md" />
          ) : tasks.length === 0 ? (
            <EmptyState
              title={S.noTasks}
              body="先建立一個任務，或改用「臨時目標」執行一次過的指令。"
              action={
                <Link to="/tasks/new">
                  <Button size="sm" variant="primary">
                    {S.newTask}
                  </Button>
                </Link>
              }
              compact
            />
          ) : (
            <div className="space-y-2">
              <Select
                value={taskId}
                onChange={(e) => onTaskId(e.target.value)}
                placeholder="選擇任務…"
                className="max-w-md"
                options={tasks.map((t) => ({
                  value: t.id,
                  label: t.enabled ? t.name : `${t.name}（${S.disabled}）`,
                }))}
              />
              {taskId && (
                <p className="max-w-2xl text-xs leading-relaxed text-[var(--color-fg-subtle)]">
                  {chosen?.goal}
                </p>
              )}

              {/* The placeholders this task leaves open. Asked for here rather than discovered
                  on the phone: a `{{PAGE_NAME}}` that reaches a device costs a model call per
                  handset to fail at step one, and no operator can fix it from a run page. */}
              {(chosen?.variables ?? []).length > 0 && (
                <div className="max-w-md space-y-2 rounded-lg border border-[var(--color-border-subtle)] bg-[rgba(255,255,255,0.02)] p-3">
                  <div className="text-[12px] font-medium">{S.fillVariables}</div>
                  <p className="text-[11px] text-[var(--color-fg-subtle)]">
                    {S.fillVariablesHint}
                  </p>
                  {(chosen?.variables ?? []).map((name) => (
                    <label key={name} className="block space-y-1">
                      <span className="font-mono text-[11px] text-[var(--color-fg-subtle)]">
                        {`{{${name}}}`}
                        {missingVariables.includes(name) && (
                          <span className="ml-1 text-[#f59e0b]">{S.variableRequired}</span>
                        )}
                      </span>
                      <Input
                        value={variables[name] ?? ""}
                        onChange={(e) => onVariable(name, e.target.value)}
                        aria-invalid={missingVariables.includes(name)}
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>
          )
        ) : (
          <div className="space-y-1.5">
            <Textarea
              value={goal}
              onChange={(e) => onGoal(e.target.value)}
              placeholder="想 AI 幫你做什麼？例如：打開 Chrome 搜尋今日天氣，然後記錄氣溫"
              className="max-w-2xl"
              rows={4}
            />
            <p className="text-[11px] text-[var(--color-fg-faint)]">{S.dispatchAdhocHint}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]",
        active
          ? "bg-[rgba(255,255,255,0.06)] text-[var(--color-fg)]"
          : "text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]",
      )}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * Step 2 — devices
 * ------------------------------------------------------------------ */

function DeviceStep({
  devices,
  loading,
  canLoadMore,
  onLoadMore,
  selected,
  onSelected,
}: {
  devices: DeviceSummary[];
  loading: boolean;
  canLoadMore: boolean;
  onLoadMore: () => void;
  selected: Set<string>;
  onSelected: (s: Set<string>) => void;
}) {
  const [tab, setTab] = useState<"individual" | "tags">("individual");
  const [term, setTerm] = useState("");

  const tags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of devices) for (const t of d.tags) counts.set(t, (counts.get(t) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [devices]);

  const filtered = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return devices;
    return devices.filter(
      (d) => d.label.toLowerCase().includes(needle) || d.id.toLowerCase().includes(needle),
    );
  }, [devices, term]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelected(next);
  };

  const tagFullySelected = (tag: string) => {
    const members = devices.filter((d) => d.tags.includes(tag));
    return members.length > 0 && members.every((d) => selected.has(d.id));
  };

  const toggleTag = (tag: string) => {
    const members = devices.filter((d) => d.tags.includes(tag)).map((d) => d.id);
    const next = new Set(selected);
    if (tagFullySelected(tag)) for (const id of members) next.delete(id);
    else for (const id of members) next.add(id);
    onSelected(next);
  };

  return (
    <Card>
      <CardContent className="space-y-3 pt-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            <ModeTab active={tab === "individual"} onClick={() => setTab("individual")}>
              {S.tabIndividual}
            </ModeTab>
            <ModeTab active={tab === "tags"} onClick={() => setTab("tags")}>
              {S.tabTags}
            </ModeTab>
          </div>
          <div className="ml-auto text-[12px] text-[var(--color-fg-subtle)]">
            {S.selectedDevices}：
            <span className="font-mono tabular-nums text-[var(--color-fg)]">{selected.size}</span>
            {selected.size > 0 && (
              <button
                type="button"
                onClick={() => onSelected(new Set())}
                className="ml-2 text-[var(--color-accent)] hover:underline"
              >
                {S.bulkClear}
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="space-y-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : tab === "individual" ? (
          <>
            <div className="flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-2 py-1.5">
              <Search size={12} className="text-[var(--color-fg-subtle)]" />
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={S.filterSearch}
                className="w-full bg-transparent text-[12px] placeholder:text-[var(--color-fg-faint)] focus:outline-none"
              />
            </div>

            <div className="max-h-[22rem] overflow-y-auto rounded-md border border-[var(--color-border-subtle)]">
              {filtered.map((d) => (
                <label
                  key={d.id}
                  className="flex cursor-pointer items-center gap-2.5 border-b border-[var(--color-border-subtle)] px-2.5 py-1.5 last:border-b-0 hover:bg-[rgba(255,255,255,0.03)]"
                >
                  <Checkbox
                    checked={selected.has(d.id)}
                    onCheckedChange={() => toggle(d.id)}
                    ariaLabel={d.label}
                  />
                  <span className="truncate text-[13px] text-[var(--color-fg)]">{d.label}</span>
                  <span className="truncate font-mono text-[10px] text-[var(--color-fg-faint)]">
                    {d.id}
                  </span>
                  <div className="ml-auto flex items-center gap-2">
                    {d.active_run && (
                      <span className="font-mono text-[10px] text-[#f59e0b]">
                        {T.stepProgress(d.active_run.n, d.active_run.max_steps)}
                      </span>
                    )}
                    <DeviceHealthPill device={d} size="sm" />
                  </div>
                </label>
              ))}
              {filtered.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-[var(--color-fg-faint)]">
                  {S.noDevicesFiltered}
                </div>
              )}
            </div>

            {canLoadMore && (
              <Button size="sm" variant="default" onClick={onLoadMore}>
                {S.loadMore}
              </Button>
            )}
          </>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {tags.length === 0 && (
              <p className="text-xs text-[var(--color-fg-faint)]">
                裝置未設定任何標籤。可在裝置詳情頁加上標籤，再用標籤批次選取。
              </p>
            )}
            {tags.map(([tag, count]) => {
              const on = tagFullySelected(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleTag(tag)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12px] transition-colors",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]",
                    on
                      ? "border-[var(--color-accent-bg)] bg-[rgba(94,106,210,0.15)] text-[var(--color-fg)]"
                      : "border-[var(--color-border)] bg-[rgba(255,255,255,0.03)] text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]",
                  )}
                >
                  <TagIcon size={11} />
                  {tag}
                  <span className="font-mono text-[10px] text-[var(--color-fg-faint)]">{count}</span>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Step 3 — timing
 * ------------------------------------------------------------------ */

function TimingStep({
  count,
  on,
  onToggle,
  batch,
  onBatch,
  intervalS,
  onIntervalS,
}: {
  count: number;
  on: boolean;
  onToggle: (v: boolean) => void;
  batch: number;
  onBatch: (v: number) => void;
  intervalS: number;
  onIntervalS: (v: number) => void;
}) {
  const waves = on && batch > 0 ? Math.ceil(count / batch) : 1;
  const totalS = on ? Math.max(0, waves - 1) * intervalS : 0;

  return (
    <Card>
      <CardContent className="space-y-4 pt-4">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-[var(--color-accent)]" />
          <span className="text-sm font-medium">{S.timingImmediate}</span>
          <span className="text-xs text-[var(--color-fg-faint)]">· {S.timingScheduledPhase2}</span>
        </div>

        <div className="rounded-lg border border-[var(--color-border-subtle)] p-3">
          <label className="flex cursor-pointer items-center gap-2">
            <Switch checked={on} onCheckedChange={onToggle} />
            <span className="text-[13px] font-medium">{S.staggerTitle}</span>
          </label>
          <p className="mt-1 pl-11 text-[11px] leading-relaxed text-[var(--color-fg-subtle)]">
            {S.staggerHint}
          </p>

          {on && (
            <div className="mt-3 flex flex-wrap items-end gap-4 pl-11">
              <div>
                <div className="mb-1 text-[11px] text-[var(--color-fg-subtle)]">{S.staggerBatch}</div>
                <Input
                  type="number"
                  min={1}
                  value={batch}
                  onChange={(e) => onBatch(Math.max(1, Number(e.target.value) || 1))}
                  className="w-24"
                />
              </div>
              <div>
                <div className="mb-1 text-[11px] text-[var(--color-fg-subtle)]">
                  {S.staggerInterval}
                </div>
                <Input
                  type="number"
                  min={1}
                  value={intervalS}
                  onChange={(e) => onIntervalS(Math.max(1, Number(e.target.value) || 1))}
                  className="w-24"
                />
              </div>
              <p className="text-[11px] text-[var(--color-fg-faint)]">
                {T.batchOf(batch, intervalS)} · 共 {waves} 批 · 約 {formatSeconds(totalS)}
              </p>
            </div>
          )}

          {!on && count > STAGGER_THRESHOLD && (
            <p className="mt-2 pl-11 text-[11px] text-[#fcd34d]">{S.staggerAutoNote}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Step 4 — review
 * ------------------------------------------------------------------ */

function ReviewStep({
  taskLabel,
  adhoc,
  count,
  stagger,
}: {
  taskLabel: string;
  adhoc: boolean;
  count: number;
  stagger: { batch: number; intervalS: number } | null;
}) {
  return (
    <Card>
      <CardContent className="space-y-2 pt-4">
        <Row label={adhoc ? S.dispatchAdhoc : S.dispatchExistingTask} value={taskLabel} />
        <Row label={S.selectedDevices} value={T.nDevices(count)} />
        <Row
          label={S.staggerTitle}
          value={stagger ? T.batchOf(stagger.batch, stagger.intervalS) : S.staggerOff}
        />
        <p className="pt-2 text-[11px] text-[var(--color-fg-faint)]">
          按「{S.dispatchFire}」會顯示實際影響範圍再確認。
        </p>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3 text-[13px]">
      <span className="w-28 shrink-0 text-[var(--color-fg-subtle)]">{label}</span>
      <span className="min-w-0 break-words text-[var(--color-fg)]">{value}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * After firing — per-device acknowledgements
 * ------------------------------------------------------------------ */

function DispatchProgress({ dispatchId, onNew }: { dispatchId: string; onNew: () => void }) {
  const status = useQuery<DispatchStatus, ApiError>({
    queryKey: qk.dispatch(dispatchId),
    queryFn: () => get<DispatchStatus>(`/dispatch/${dispatchId}`),
    // The socket carries the acknowledgements; this is the reconciler behind it.
    refetchInterval: 15_000,
  });

  const [acks, setAcks] = useState<Map<string, DispatchFrame>>(new Map());

  useTopic(
    topicDispatch(dispatchId),
    useCallback((d: unknown) => {
      const frame = d as DispatchFrame;
      if (frame?.kind !== "dispatch.ack") return;
      setAcks((prev) => {
        const next = new Map(prev);
        next.set(frame.device_id, frame);
        return next;
      });
    }, []),
  );

  const cancel = useApiMutation<void, void>(
    () => post<void>(`/dispatch/${dispatchId}/cancel`),
    {
      invalidates: [qk.dispatch(dispatchId)],
      successMessage: S.dispatchCancelled,
      errorTitle: S.mutationFailed,
    },
  );

  const rows = useMemo(() => {
    const base = status.data?.devices ?? [];
    return base.map((r) => {
      const ack = acks.get(r.device_id);
      return ack ? { ...r, state: ack.state, run_id: ack.run_id ?? r.run_id } : r;
    });
  }, [status.data, acks]);

  const counts = status.data?.counts;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold tracking-[-0.24px]">{S.dispatchAckTitle}</h2>
        <span className="font-mono text-xs text-[var(--color-fg-faint)]">{dispatchId}</span>
        <div className="ml-auto flex gap-2">
          <Guarded permission="dispatch_run">
            <Button size="sm" variant="warn" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
              {S.dispatchCancel}
            </Button>
          </Guarded>
          <Button size="sm" variant="default" onClick={onNew}>
            {S.dispatchTitle}
          </Button>
        </div>
      </div>

      <p className="text-xs text-[var(--color-fg-subtle)]">{S.dispatchAckHint}</p>

      {counts && (
        <div className="flex flex-wrap gap-2">
          {(Object.entries(counts) as Array<[keyof typeof counts, number]>)
            .filter(([, n]) => n > 0)
            .map(([state, n]) => (
              <span
                key={state}
                className="rounded-md border border-[var(--color-border-subtle)] bg-[rgba(255,255,255,0.02)] px-2 py-1 text-[12px]"
              >
                {dispatchStateLabel(state)}{" "}
                <span className="font-mono tabular-nums text-[var(--color-fg)]">{n}</span>
              </span>
            ))}
        </div>
      )}

      {status.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <div className="max-h-[26rem] overflow-y-auto rounded-lg border border-[var(--color-border-subtle)]">
          {rows.map((r) => (
            <div
              key={r.device_id}
              className="flex items-center gap-2.5 border-b border-[var(--color-border-subtle)] px-3 py-1.5 text-[12px] last:border-b-0"
            >
              <span className="font-mono text-[11px] text-[var(--color-fg-muted)]">{r.device_id}</span>
              <span className="ml-auto text-[var(--color-fg-subtle)]">
                {dispatchStateLabel(r.state)}
              </span>
              {r.run_id && (
                <Tooltip content="開啟 run">
                  <Link
                    to={`/runs/${r.run_id}`}
                    className="font-mono text-[11px] text-[var(--color-accent)] hover:underline"
                  >
                    {r.run_id.slice(0, 12)}…
                  </Link>
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
